/**
 * מתאם (Adapter) המגשר בין מערכת שדה הראייה הישנה מבוססת הגריד 
 * למערכת החדשה מבוססת קטעים
 * 
 * מאפשר להשתמש בפונקציונליות החדשה מבלי לשבור את הקוד הקיים
 */

import WallSegment from './WallSegment';
import WallSegmentCollection from './WallSegmentCollection';
import LineBasedFOV from './LineBasedFOV';
// import polygonClipping from 'polygon-clipping'; // Commented: unused import
// import * as PolygonUtils from './PolygonUtils'; // Commented: unused import

// Quality presets for FOV calculation (ray count)
const QUALITY_PRESETS = {
  LOW: 360,     // Good for performance, less smooth edges
  MEDIUM: 720,  // Default, balanced quality/performance
  HIGH: 1080,   // Higher quality, more resource intensive
  ULTRA: 1440   // Very high quality, may impact performance
};

// Performance settings for different devices/resolutions
const PERFORMANCE_SETTINGS = {
  LOW: {      // For low-end devices or large maps
    cullMargin: 150,
    rayCount: QUALITY_PRESETS.LOW,
    enableEdgeCaseHandling: true,
    minSegmentLength: 2,
    optimizationsInterval: 10 // How many operations before doing optimization
  },
  MEDIUM: {   // Default balanced settings
    cullMargin: 200,
    rayCount: QUALITY_PRESETS.MEDIUM,
    enableEdgeCaseHandling: true,
    minSegmentLength: 0.5,
    optimizationsInterval: 20
  },
  HIGH: {     // High-end systems
    cullMargin: 250,
    rayCount: QUALITY_PRESETS.HIGH,
    enableEdgeCaseHandling: true,
    minSegmentLength: 0.1,
    optimizationsInterval: 30
  }
};

class LineBasedFOVAdapter {
  /**
   * יוצר מתאם חדש
   * @param {Object} options - אפשרויות אתחול
   * @param {boolean} options.enabled - האם להשתמש במערכת החדשה (ברירת מחדל: true)
   * @param {string} options.quality - איכות ה-FOV: 'LOW', 'MEDIUM', 'HIGH', 'ULTRA'
   * @param {boolean} options.enableCulling - האם להפעיל culling לקטעים מחוץ למסך (ברירת מחדל: true)
   * @param {string} options.performanceProfile - פרופיל ביצועים: 'LOW', 'MEDIUM', 'HIGH'
   */
  constructor(options = {}) {
    this.enabled = options.enabled !== undefined ? options.enabled : true;
    this.quality = options.quality || 'MEDIUM';
    this.enableCulling = options.enableCulling !== undefined ? options.enableCulling : true;
    this.performanceProfile = options.performanceProfile || 'MEDIUM';
    
    // הגדרות ביצועים
    this.perfSettings = PERFORMANCE_SETTINGS[this.performanceProfile] || PERFORMANCE_SETTINGS.MEDIUM;
    
    // יצירת האוסף והמודל
    this.segmentCollection = new WallSegmentCollection();
    
    // Initialize FOV with the appropriate ray count based on quality
    const rayCount = QUALITY_PRESETS[this.quality] || QUALITY_PRESETS.MEDIUM;
    this.fov = new LineBasedFOV({ 
      rayCount,
      enableCulling: this.enableCulling,
      cullMargin: this.perfSettings.cullMargin
    });
    
    // מטמון תוצאות חישוב FOV
    this.cache = {
      visibilityPolygon: [],
      origin: { x: 0, y: 0 },
      radius: 0,
      wallsHash: '',
      isValid: false,
      quality: this.quality // Track quality in cache to invalidate when changed
    };
    
    // Viewport bounds for culling
    this.viewportBounds = { x: 0, y: 0, width: 5000, height: 5000 };
    
    // היסטוריית שינויים אחרונה
    this.lastChangedSegmentIds = [];
    
    // מונה פעולות לצורך אופטימיזציה תקופתית
    this.operationsCounter = 0;
    
    // Track scale changes for zoom detection
    this.lastScale = 1;
    
    // Add throttling to prevent multiple cache clears too close together
    this.lastCacheClearTime = 0;
  }
  
  /**
   * נקה את כל המטמונים במערכת
   * פונקציה זו נקראת בעת פעולות שעשויות להשפיע על תקינות המטמון
   * כמו שינויי זום, שינויי איכות, וכד'
   */
  clearCache() {
    // נקה את המטמון העיקרי
    this.cache = {
      visibilityPolygon: [],
      origin: { x: 0, y: 0 },
      radius: 0,
      wallsHash: '',
      isValid: false,
      quality: this.quality
    };
    
    // קרא לפונקציית ניקוי המטמון של ה-FOV
    if (this.fov && typeof this.fov.clearCache === 'function') {
      this.fov.clearCache();
    }
    
    // נקה את מטמון האוסף
    if (this.segmentCollection && typeof this.segmentCollection.invalidateCache === 'function') {
      this.segmentCollection.invalidateCache();
    }
    
    // עדכן מחדש את הקטעים ב-FOV (חשוב במיוחד אחרי שינויי זום)
    if (this.fov && this.segmentCollection) {
      this.fov.setWalls(this.segmentCollection.segments);
    }
    
    // העבר את גבולות החלון הנראה מחדש ל-FOV
    if (this.fov && this.viewportBounds) {
      this.fov.setViewportBounds(this.viewportBounds);
    }
    
    return this;
  }
  
  /**
   * מפעיל או מכבה את המערכת החדשה
   * @param {boolean} enabled - האם להפעיל
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }
  
  /**
   * מגדיר את פרופיל הביצועים
   * @param {string} profile - פרופיל ביצועים: 'LOW', 'MEDIUM', 'HIGH'
   */
  setPerformanceProfile(profile) {
    if (PERFORMANCE_SETTINGS[profile]) {
      this.performanceProfile = profile;
      this.perfSettings = PERFORMANCE_SETTINGS[profile];
      
      // עדכון המערכות
      if (this.fov) {
        this.fov = new LineBasedFOV({
          rayCount: QUALITY_PRESETS[this.quality] || QUALITY_PRESETS.MEDIUM,
          enableCulling: this.enableCulling,
          cullMargin: this.perfSettings.cullMargin
        });
      }
      
      // ביצוע אופטימיזציה של הקטעים לפי הפרופיל החדש
      this.optimizeSegments();
      
      // איפוס מטמון
      this.cache.isValid = false;
    }
  }
  
  /**
   * מגדיר את איכות חישוב ה-FOV
   * @param {string} quality - רמת האיכות: 'LOW', 'MEDIUM', 'HIGH', 'ULTRA'
   */
  setQuality(quality) {
    if (QUALITY_PRESETS[quality]) {
      this.quality = quality;
      
      // Update the FOV model with new ray count
      const rayCount = QUALITY_PRESETS[quality];
      this.fov = new LineBasedFOV({ 
        rayCount,
        enableCulling: this.enableCulling,
        cullMargin: this.perfSettings.cullMargin
      });
      
      // Invalidate cache since quality changed
      this.cache.isValid = false;
    }
  }
  
  /**
   * מגדיר את גבולות החלון הנראה לצורך culling
   * @param {Object} bounds - גבולות החלון {x, y, width, height}
   */
  setViewportBounds(bounds) {
    if (bounds && typeof bounds.x === 'number' && typeof bounds.y === 'number' &&
        typeof bounds.width === 'number' && typeof bounds.height === 'number') {
      
      // Save segment data before viewport change to ensure consistency
      const originalSegments = this.segmentCollection ? 
        this.segmentCollection.segments.map(segment => ({ ...segment })) : 
        [];
        
      // console.log('[FOVAdapter] setViewportBounds called', {
      //   currentBounds: this.viewportBounds,
      //   newBounds: bounds
      // });
      
      // חישוב יחס הסקייל הנוכחי
      let currentScale = 1;
      if (this.viewportBounds && this.viewportBounds.width && bounds.width) {
        // Use the viewport width as an indicator of the scale
        const prevWidth = this.viewportBounds.width;
        const newWidth = bounds.width;
        currentScale = prevWidth / newWidth;
      }
      
      // console.log('[FOVAdapter] Scale calculation:', {
      //   prevWidth: this.viewportBounds ? this.viewportBounds.width : 'unknown',
      //   newWidth: bounds.width,
      //   calculated: currentScale,
      //   lastScale: this.lastScale
      // });
      
      // בדיקה אם היה שינוי משמעותי בסקייל (זום)
      const scaleChange = Math.abs(currentScale - this.lastScale);
      const significantScaleChange = scaleChange > 0.01; // 1% שינוי מספיק לאיפוס
      
      // console.log('[FOVAdapter] Scale change:', {
      //   scaleChange,
      //   significantScaleChange,
      //   currentScale,
      //   lastScale: this.lastScale
      // });
      
      // Add throttling to prevent multiple cache clears too close together
      const now = Date.now();
      const isThrottled = (now - this.lastCacheClearTime) < 500; // 500ms minimum between cache clears
      
      // console.log('[FOVAdapter] Throttling:', {
      //   now,
      //   lastCacheClearTime: this.lastCacheClearTime,
      //   isThrottled,
      //   timeSinceLastClear: now - this.lastCacheClearTime
      // });
      
      // שמירת הגבולות החדשים
      this.viewportBounds = { ...bounds };
      
      // Log segments before applying bounds
      // console.log('[FOVAdapter] Segments before setting bounds:', {
      //   count: this.segmentCollection ? this.segmentCollection.segments.length : 0,
      //   first3: this.segmentCollection ? this.segmentCollection.segments.slice(0, 3) : []
      // });
      
      // עדכן את גבולות החלון במערכת הFOV
      if (this.fov && this.enabled && this.enableCulling) {
        this.fov.setViewportBounds(this.viewportBounds);
      }
      
      // Log segments after applying bounds
      // console.log('[FOVAdapter] Segments after setting bounds:', {
      //   count: this.segmentCollection ? this.segmentCollection.segments.length : 0,
      //   first3: this.segmentCollection ? this.segmentCollection.segments.slice(0, 3) : []
      // });
      
      // CRITICAL: Ensure segments are preserved after viewport update
      if (originalSegments.length > 0 && this.segmentCollection) {
        // Check if segment count changed unexpectedly
        if (this.segmentCollection.segments.length !== originalSegments.length) {
          // console.log('[FOVAdapter] Segment count changed unexpectedly during viewport update. Restoring original segments.');
          // Restore segments
          this.segmentCollection.segments = [...originalSegments];
          if (this.fov) {
            this.fov.setWalls(originalSegments);
          }
        }
      }
      
      // אם היה שינוי סקייל משמעותי ולא בזמן throttling, נקה את המטמון
      if (significantScaleChange && !isThrottled) {
        // console.log('[FOVAdapter] Clearing cache due to significant scale change');
        this.clearCache();
        
        // עדכון זמן הניקוי האחרון
        this.lastCacheClearTime = now;
      } else {
        // console.log('[FOVAdapter] No cache clear needed:', {
        //   significantScaleChange,
        //   isThrottled
        // });
      }
      
      // שמור את הסקייל החדש להשוואה הבאה
      this.lastScale = currentScale;
      
      // console.log('[FOVAdapter] After cache clear:', {
      //   segments: this.segmentCollection ? this.segmentCollection.segments.length : 0,
      //   visibilityPolygon: this.fov ? this.fov.visibilityPolygon.length : 0,
      //   newScale: currentScale
      // });
    }
  }
  
  /**
   * מפעיל או מכבה את culling הקטעים שמחוץ למסך
   * @param {boolean} enabled - האם להפעיל culling
   */
  setCullingEnabled(enabled) {
    this.enableCulling = enabled;
    
    if (this.fov) {
      // Pass option to FOV system
      if (typeof this.fov.setOption === 'function') {
        this.fov.setOption('enableCulling', enabled);
      } else {
        // If setOption not available, recreate FOV with new setting
        const rayCount = QUALITY_PRESETS[this.quality] || QUALITY_PRESETS.MEDIUM;
        this.fov = new LineBasedFOV({ 
          rayCount,
          enableCulling: enabled,
          cullMargin: this.perfSettings.cullMargin
        });
        
        // Reapply viewport bounds
        this.fov.setViewportBounds(this.viewportBounds);
        
        // Reset walls
        this.fov.setWalls(this.segmentCollection.segments);
      }
      
      // Invalidate cache
      this.cache.isValid = false;
    }
  }
  
  /**
   * מבצע אופטימיזציה של קטעי הקיר
   * @returns {Object} מידע על האופטימיזציה שבוצעה
   */
  optimizeSegments() {
    if (!this.enabled || !this.perfSettings.enableEdgeCaseHandling) {
      return { optimized: false };
    }
    
    const minLength = this.perfSettings.minSegmentLength;
    
    // ביצוע אופטימיזציה באמצעות אוסף הקטעים
    const stats = this.segmentCollection.handleEdgeCases({
      mergeOverlapping: true,
      removeShort: true,
      minLength
    });
    
    // עדכון מספר פעולות האופטימיזציה
    this.operationsCounter = 0;
    
    // עדכון המידע ב-FOV
    if (stats.mergedSegments > 0 || stats.removedShortSegments > 0) {
      this.fov.setWalls(this.segmentCollection.segments);
      this.cache.isValid = false;
    }
    
    return {
      optimized: stats.mergedSegments > 0 || stats.removedShortSegments > 0,
      ...stats
    };
  }
  
  /**
   * בודק אם צריך לבצע אופטימיזציה תקופתית
   * @private
   */
  checkPeriodicOptimization() {
    if (!this.enabled || !this.perfSettings.enableEdgeCaseHandling) {
      return;
    }
    
    // בדיקה אם הגיע הזמן לאופטימיזציה
    this.operationsCounter++;
    if (this.operationsCounter >= this.perfSettings.optimizationsInterval) {
      this.optimizeSegments();
    }
  }
  
  /**
   * ממיר נתוני קירות מבוססי רשימת נקודות למערך קטעים
   * @param {Object} line - אובייקט קו עם נקודות
   * @returns {Array<WallSegment>} מערך של קטעי קיר
   */
  convertLineToSegments(line) {
    const segments = [];
    
    if (!line || !line.points || line.points.length < 4) {
      return segments;
    }
    
    // הגדרת מאפיינים משותפים
    const options = {
      thickness: line.strokeWidth || 2,
      color: line.color || '#000000',
      type: line.tool || 'wall',
      parentId: line.id
    };
    
    // יצירת קטעים מרשימת נקודות
    for (let i = 0; i < line.points.length - 3; i += 2) {
      const segment = new WallSegment(
        line.points[i], 
        line.points[i + 1], 
        line.points[i + 2], 
        line.points[i + 3], 
        options
      );
      
      segments.push(segment);
    }
    
    return segments;
  }
  
  /**
   * ממיר מלבן למערך של 4 קטעים
   * @param {Object} rectangle - אובייקט מלבן
   * @returns {Array<WallSegment>} מערך של קטעי קיר
   */
  convertRectangleToSegments(rectangle) {
    if (!rectangle || !rectangle.width || !rectangle.height) {
      return [];
    }
    
    const { x, y, width, height } = rectangle;
    
    // הגדרת מאפיינים משותפים
    const options = {
      thickness: rectangle.strokeWidth || 2,
      color: rectangle.color || '#000000',
      type: 'rectangle',
      parentId: rectangle.id
    };
    
    // יצירת 4 קטעים למלבן
    return [
      new WallSegment(x, y, x + width, y, options), // צלע עליונה
      new WallSegment(x + width, y, x + width, y + height, options), // צלע ימנית
      new WallSegment(x + width, y + height, x, y + height, options), // צלע תחתונה
      new WallSegment(x, y + height, x, y, options) // צלע שמאלית
    ];
  }
  
  /**
   * ממיר עיגול למערך קטעים
   * @param {Object} circle - אובייקט עיגול
   * @param {number} numSegments - מספר קטעים לקירוב העיגול (אופציונלי)
   * @returns {Array<WallSegment>} מערך של קטעי קיר
   */
  convertCircleToSegments(circle, numSegments = 36) {
    if (!circle || !circle.radius) {
      return [];
    }
    
    const { x, y, radius } = circle;
    
    // הגדרת מאפיינים משותפים
    const options = {
      thickness: circle.strokeWidth || 2,
      color: circle.color || '#000000',
      type: 'circle',
      parentId: circle.id
    };
    
    const segments = [];
    
    // יצירת קטעים לקירוב העיגול
    for (let i = 0; i < numSegments; i++) {
      const angle1 = (i / numSegments) * 2 * Math.PI;
      const angle2 = ((i + 1) / numSegments) * 2 * Math.PI;
      
      const x1 = x + radius * Math.cos(angle1);
      const y1 = y + radius * Math.sin(angle1);
      const x2 = x + radius * Math.cos(angle2);
      const y2 = y + radius * Math.sin(angle2);
      
      segments.push(new WallSegment(x1, y1, x2, y2, options));
    }
    
    return segments;
  }
  
  /**
   * ממיר פוליגון למערך קטעים
   * @param {Object} polygon - אובייקט פוליגון
   * @returns {Array<WallSegment>} מערך של קטעי קיר
   */
  convertPolygonToSegments(polygon) {
    if (!polygon || !polygon.points || polygon.points.length < 6) {
      return [];
    }
    
    // הגדרת מאפיינים משותפים
    const options = {
      thickness: polygon.strokeWidth || 2,
      color: polygon.color || '#000000',
      type: 'polygon',
      parentId: polygon.id
    };
    
    const segments = [];
    
    // יצירת קטעים מנקודות הפוליגון
    for (let i = 0; i < polygon.points.length - 2; i += 2) {
      segments.push(
        new WallSegment(
          polygon.points[i], 
          polygon.points[i + 1], 
          polygon.points[i + 2], 
          polygon.points[i + 3], 
          options
        )
      );
    }
    
    // סגירת הפוליגון אם צריך
    if (polygon.closed !== false && segments.length > 0) {
      // בדיקה אם הפוליגון כבר סגור
      const firstPoint = { x: polygon.points[0], y: polygon.points[1] };
      const lastPoint = { 
        x: polygon.points[polygon.points.length - 2], 
        y: polygon.points[polygon.points.length - 1] 
      };
      
      // אם נקודת ההתחלה והסיום שונות, סגור את הפוליגון
      if (firstPoint.x !== lastPoint.x || firstPoint.y !== lastPoint.y) {
        segments.push(
          new WallSegment(
            lastPoint.x, 
            lastPoint.y, 
            firstPoint.x, 
            firstPoint.y, 
            options
          )
        );
      }
    }
    
    return segments;
  }
  
  /**
   * ממצה קטעי קיר משכבות גלויות
   * יוצר את קטעי הקיר רק מהאלמנטים הנראים, ללא התחשבות בכלי המחק
   * יישום לגישת "מה שאתה רואה הוא מה שאתה מקבל" (WYSIWYG)
   * @param {Array<Object>} layers - מערך השכבות
   * @returns {Object} אובייקט המכיל קטעי קיר ואובייקטים מיוחדים
   */
  extractWallSegmentsFromVisibleLayers(layers) {
    if (!this.enabled) return { blockingSegments: [], specialObjects: [] };
    
    // const startTime = performance.now(); // Commented: unused debug variable
    // console.log('[FOVAdapter] extractWallSegmentsFromVisibleLayers started. Layers count:', layers.length);
    
    // מערך חדש לאחסון כל הקטעים החוסמים ורשימה לאובייקטים מיוחדים
    const allBlockingSegments = [];
    let specialLOSObjects = [];
    
    // עיבוד שכבות לפי סדר
    const orderedLayers = [...layers].sort((a, b) => a.layer_order - b.layer_order);
    
    // נתחיל בבניית מפה של כל האזורים הגלויים
    // אנחנו נשתמש באזורים הללו כדי לקבוע מה נראה חזותית
    const visibleAreas = new Map(); // מפתח: זהות שכבה, ערך: מערך של אובייקטים גלויים
    
    // עבור כל שכבה, נאסוף את כל האלמנטים הנראים (לא כולל מחקים)
    orderedLayers.forEach(layer => {
      // דלג על שכבות שאינן מוצגות או אינן שכבות קיר
      if (layer.is_visible === false) return;
      if (!layer.is_wall_layer) return;

      try {
        const layerData = JSON.parse(layer.layer_data || '{"lines":[]}');
        const layerLines = layerData.lines || [];
        
        // סנן את כל האלמנטים הנראים (ללא מחקים)
        const visibleElements = layerLines.filter(line => line.tool !== 'eraser');
        
        if (visibleElements.length > 0) {
          visibleAreas.set(layer.id, visibleElements);
        }
      } catch (err) {
        console.error('Error processing layer for extracting visible elements:', err);
      }
    });
    
    // עכשיו נמיר את כל האלמנטים הנראים לקטעי קיר
    for (const [, elements] of visibleAreas.entries()) { // layerId unused, replaced with _
      for (const element of elements) {
        // בדיקה אם האלמנט הוא אובייקט LOS מיוחד
        if ((element.tool === 'rectangle' || element.tool === 'circle' || element.tool === 'polygon') && element.isLOSObject === true) {
          // זהו אובייקט LOS מיוחד, נוסיף אותו לרשימה הייעודית
              specialLOSObjects.push({ 
            ...element, 
            shapeType: element.tool, // 'rectangle', 'circle', או 'polygon'
            id: element.id || `los-${Date.now()}-${Math.random()}` // וידוא מזהה ייחודי
              });
            } else {
          // זהו קיר רגיל או צורה אטומה, נמיר לקטעים
              let segments = [];
          if (element.tool === 'wall' || element.tool === 'pencil') {
            segments = this.convertLineToSegments(element);
          } else if (element.tool === 'rectangle') {
            segments = this.convertRectangleToSegments(element);
          } else if (element.tool === 'circle') {
            segments = this.convertCircleToSegments(element);
          } else if (element.tool === 'polygon') {
            segments = this.convertPolygonToSegments(element);
              }
              allBlockingSegments.push(...segments);
            }
          }
    }
    
    // const endTime = performance.now(); // Commented: unused debug variable
    // console.log('[FOVAdapter] extractWallSegmentsFromVisibleLayers completed. Duration:', (endTime - startTime) + 'ms', 'Segments:', allBlockingSegments.length, 'Special objects:', specialLOSObjects.length);
    
    return {
      blockingSegments: allBlockingSegments,
      specialObjects: specialLOSObjects
    };
  }
  
  /**
   * עדכון קטעי הקיר משכבות הציור
   * @param {Array<Object>} layers - מערך השכבות
   */
  updateWallsFromLayers(layers) {
    if (!this.enabled) return;
    
    // const startTime = performance.now(); // Commented: unused debug variable
    // console.log('[FOVAdapter] updateWallsFromLayers started. Layers count:', layers.length);

    // קבל את קטעי הקיר והאובייקטים המיוחדים מהשכבות הגלויות
    // במקום המימוש המורכב הקודם, נשתמש בגישת "מה שאתה רואה הוא מה שאתה מקבל"
    const { blockingSegments, specialObjects } = this.extractWallSegmentsFromVisibleLayers(layers);
    
    // עדכון אוסף הקטעים החוסמים עם הקטעים החדשים שנמצאו
    this.segmentCollection = new WallSegmentCollection(blockingSegments);
    
    // עדכון מודל הFOV עם הקטעים החוסמים והאובייקטים המיוחדים
    this.fov.setWalls(this.segmentCollection.segments);
    this.fov.setSpecialObjects(specialObjects); // העבר אובייקטים מיוחדים למודל FOV
        
    // console.log('[FOVAdapter] Walls set in FOV model. Count:', this.fov.walls.length);
    // console.log('[FOVAdapter] Special Objects set in FOV model. Count:', this.fov.specialObjects.length);
    
    this.checkPeriodicOptimization();
    this.fov.setViewportBounds(this.viewportBounds);
    
    // תמיד נקה את המטמון לחלוטין כדי להבטיח התנהגות עקבית
    // זה חשוב במיוחד כדי לפתור בעיות של חסימת אור אחרי מחיקה וציור מחדש
    this.clearCache();
    
    // const endTime = performance.now(); // Commented: unused debug variable
    // console.log('[FOVAdapter] updateWallsFromLayers completed. Duration:', (endTime - startTime) + 'ms');
  }
  
  /**
   * עדכון קטעי קיר בעקבות מחיקה
   * @param {Array<Object>} eraserPath - מסלול המחיקה
   * @returns {Array<string>} מזהים של קטעים חדשים שנוצרו
   */
  eraseWalls(eraserPath) {
    if (!this.enabled || !eraserPath || eraserPath.length === 0) {
      return [];
    }
    
    // במקום לבצע מחיקה מורכבת ולעקוב אחר כל קטע,
    // אנחנו פשוט מנקים את המטמון ומאפשרים למערכת לחשב מחדש את כל הקטעים
    // בהתאם לגישה החדשה של "מה שאתה רואה הוא מה שאתה מקבל"
    
    // במערכת החדשה, הפעולה הזו תקרא לרוב מהמפה, ואז הניקוי המלא יקרה ב-updateWallsFromLayers
    // אבל אנחנו מבצעים גם כאן ניקוי מטמון מלא למקרה שהפונקציה נקראת ישירות
    this.clearCache();
    
    // אנחנו מחזירים מערך ריק של מזהים, כי בגישה החדשה אין מעקב אחר קטעים ספציפיים
    this.lastChangedSegmentIds = [];
    
    return this.lastChangedSegmentIds;
  }
  
  /**
   * חישוב שדה הראייה ממיקום ספציפי
   * @param {number} x - קואורדינטת X
   * @param {number} y - קואורדינטת Y
   * @param {number} radius - רדיוס הראייה
   * @returns {Array<number>} מערך שטוח של נקודות פוליגון הראייה
   */
  calculateVisibility(x, y, radius) {
    if (!this.enabled) {
      return []; // החזר פוליגון ריק כשהמערכת כבויה
    }
    
    // חישוב עם שימוש במטמון
    return this.fov.calculateWithCache(x, y, radius, this.cache);
  }
  
  /**
   * בדיקה אם נקודה נראית
   * @param {number} x - קואורדינטת X
   * @param {number} y - קואורדינטת Y
   * @returns {boolean} האם הנקודה נראית
   */
  isPointVisible(x, y) {
    if (!this.enabled) {
      return true; // כשהמערכת כבויה, הכל נראה
    }
    
    return this.fov.isPointVisible(x, y);
  }
  
  /**
   * מחזיר את אוסף הקטעים הנוכחי
   * @returns {WallSegmentCollection} אוסף הקטעים
   */
  getSegmentCollection() {
    return this.segmentCollection;
  }
  
  /**
   * מחזיר את מודל הFOV הנוכחי
   * @returns {LineBasedFOV} מודל הFOV
   */
  getFOV() {
    return this.fov;
  }
}

export default LineBasedFOVAdapter; 